import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const HEARTBEAT_INTERVAL_MS = 30000;

const createOwnerToken = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export default function useRecordLock({ baseURL, userName }) {
  const ownerTokenRef = useRef(createOwnerToken());
  const activeLockRef = useRef(null);

  const releaseWithBeacon = useCallback(() => {
    const activeLock = activeLockRef.current;
    if (!activeLock || !baseURL) return;

    const url = `${baseURL}/record-lock/${activeLock.recordId}/release`;
    const body = JSON.stringify({ owner_token: ownerTokenRef.current });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  }, [baseURL]);

  const releaseRecordLock = useCallback(async () => {
    const activeLock = activeLockRef.current;
    if (!activeLock || !baseURL) return;
    activeLockRef.current = null;
    try {
      await axios.post(`${baseURL}/record-lock/${activeLock.recordId}/release`, {
        owner_token: ownerTokenRef.current,
      });
    } catch {
      // The expiring server lease is the fallback when an immediate release fails.
    }
  }, [baseURL]);

  const acquireRecordLock = useCallback(async (recordId, workflow) => {
    if (!recordId || !baseURL) return false;

    const previousLock = activeLockRef.current;

    try {
      await axios.post(`${baseURL}/record-lock/${recordId}/acquire`, {
        owner_token: ownerTokenRef.current,
        locked_by: userName,
        workflow,
      });
      activeLockRef.current = { recordId, workflow };
      if (previousLock && String(previousLock.recordId) !== String(recordId)) {
        axios.post(`${baseURL}/record-lock/${previousLock.recordId}/release`, {
          owner_token: ownerTokenRef.current,
        }).catch(() => {});
      }
      return true;
    } catch (error) {
      const lock = error?.response?.data || {};
      await Swal.fire({
        icon: 'warning',
        title: 'Record is already open',
        text: `${lock.locked_by || 'Another user'} currently has this record open in ${lock.workflow || 'another screen'}. Try again after they close or save it.`,
      });
      return false;
    }
  }, [baseURL, userName]);

  useEffect(() => {
    const heartbeat = window.setInterval(() => {
      const activeLock = activeLockRef.current;
      if (!activeLock || !baseURL) return;
      axios.post(`${baseURL}/record-lock/${activeLock.recordId}/heartbeat`, {
        owner_token: ownerTokenRef.current,
      }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);

    const handlePageExit = () => releaseWithBeacon();
    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('beforeunload', handlePageExit);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', handlePageExit);
      window.removeEventListener('beforeunload', handlePageExit);
      releaseWithBeacon();
    };
  }, [baseURL, releaseWithBeacon]);

  return { acquireRecordLock, releaseRecordLock };
}
